// Shared standings visual building blocks — extracted VERBATIM from
// components/home/StandingsWidget.js (Competitions build, 2026-08-17) so the
// Competitions pages can reuse them without duplicating the widget-local
// components (FE-RECON §4: "ein Modul, kein Duplikat"). StandingsWidget now
// imports from here; behaviour and pixel output are identical to the
// pre-extraction widget.
import React, { useState, useEffect } from "react";

// Token subset the extracted components depend on (values verbatim from the
// widget's TP palette — the widget keeps its own TP object for everything
// else).
const TP = {
  primary: "#4A90D9",
  primaryDark: "#2F78C4",
  primarySoft: "#EBF3FF",
  border: "#E5E7EB",
  text3: "#9CA3AF",
  win: "#10B981",
  draw: "#9CA3AF",
  loss: "#EF4444",
};

export function initials(name, short) {
  const src = (short || name || "?").trim();
  if (!src) return "??";
  const parts = src.split(/\s+/).slice(0, 2);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

// Windows Chrome/Edge ship no colour flag emoji, so bare 🇪🇸 etc. render as
// either the bare regional-indicator letters ("ES") or two empty boxes —
// either way inconsistent. Render a small letter chip instead on those
// platforms; everywhere else the emoji is fine.
export const IS_WINDOWS = (() => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  return /Win/i.test(platform) || /Windows/i.test(ua);
})();

export function Flag({ league, size = 18 }) {
  if (!league) return null;
  const code = (league.country_code || "").slice(0, 3).toUpperCase();
  if (IS_WINDOWS || !league.flag) {
    return (
      <span
        className="inline-flex items-center justify-center font-extrabold tracking-wider rounded-sm flex-shrink-0"
        style={{
          minWidth: size + 6,
          height: size,
          fontSize: Math.max(9, Math.floor(size * 0.55)),
          padding: "0 4px",
          background: TP.primarySoft,
          color: TP.primaryDark,
        }}
        aria-hidden="true"
      >
        {code || "?"}
      </span>
    );
  }
  return (
    <span
      className="inline-block leading-none flex-shrink-0"
      style={{ fontSize: size }}
      aria-hidden="true"
    >
      {league.flag}
    </span>
  );
}

export function FormDot({ result }) {
  const color = result === "W" ? TP.win : result === "D" ? TP.draw : TP.loss;
  const title = result === "W" ? "Win" : result === "D" ? "Draw" : "Loss";
  return (
    <span
      title={title}
      className="inline-block w-2 h-2 rounded-full"
      style={{ background: color }}
    />
  );
}

export function FormGuide({ form }) {
  if (!form || form.length === 0) return <span className="text-[11px]" style={{ color: TP.text3 }}>—</span>;
  return (
    <div className="flex items-center gap-1 justify-end">
      {form.map((r, i) => <FormDot key={i} result={r} />)}
    </div>
  );
}

export function TeamLogo({ team, size = 24 }) {
  const [errored, setErrored] = useState(false);
  useEffect(() => { setErrored(false); }, [team?.logo]);
  const hasLogo = Boolean(team?.logo) && !errored;
  if (hasLogo) {
    return (
      <img
        src={team.logo}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        onError={() => setErrored(true)}
        className="rounded-md object-contain flex-shrink-0 bg-white"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="flex items-center justify-center text-white font-extrabold flex-shrink-0"
      style={{
        width: size, height: size, borderRadius: 6, fontSize: size * 0.42,
        background: `linear-gradient(135deg, ${TP.primary} 0%, ${TP.primaryDark} 100%)`,
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
      }}
      aria-hidden="true"
    >
      {initials(team?.name, team?.short_name)}
    </div>
  );
}

export function CompetitionLogo({ league, size = 36 }) {
  const [errored, setErrored] = useState(false);
  useEffect(() => { setErrored(false); }, [league?.logo]);
  if (league?.logo && !errored) {
    return (
      <img
        src={league.logo}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        onError={() => setErrored(true)}
        className="rounded-lg object-contain flex-shrink-0 bg-white border"
        style={{ width: size, height: size, borderColor: TP.border }}
      />
    );
  }
  return (
    <div
      className="flex items-center justify-center font-bold flex-shrink-0"
      style={{
        width: size, height: size, borderRadius: size * 0.25,
        background: TP.primarySoft, color: TP.primaryDark,
        fontSize: size * 0.48,
      }}
      aria-hidden="true"
    >
      {IS_WINDOWS || !league?.flag
        ? (league?.country_code || "").slice(0, 3)
        : league.flag}
    </div>
  );
}
